import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { ApiErrorService } from 'src/app/core/services/api-error.service';
import { CHAT_STRINGS_PT, ChatStrings } from '../chat.strings';
import { ChatAutoReply, ChatAutoReplyTrigger, ChatQuickReply } from '../chat.model';
import { CHAT_NO_CAPABILITIES, ChatCapabilities, ChatService } from '../services/chat.service';

/**
 * A ordem das linhas na maqueta `Automaticas.dc.html`. O gatilho `unanswered`
 * (Sem resposta) fica escondido até o servidor o disparar (2026-10-28); regras
 * já guardadas com esse gatilho continuam na base, só não aparecem aqui.
 */
export const CHAT_AUTO_REPLY_TRIGGERS: ChatAutoReplyTrigger[] = ['first_contact', 'out_of_hours', 'away'];

/** Uma linha (gatilho x âmbito) do formulário de respostas automáticas. */
export interface ChatAutoReplyRow {
  trigger: ChatAutoReplyTrigger;
  scope: 'general' | 'mine';
  /** A resposta guardada no servidor, se existir. */
  existing: ChatAutoReply | null;
  message: string;
  status: boolean;
  dirty: boolean;
  saving: boolean;
  saved: boolean;
}

/** O formulário do modal de resposta rápida. */
interface QuickReplyForm {
  id: string | null;
  shortcut: string;
  message: string;
  status: boolean;
  general: boolean;
}

/**
 * `chat/settings`: respostas automáticas (uma linha por gatilho, toggle +
 * texto, como a maqueta) e respostas rápidas (atalho / mensagem, criar,
 * editar, apagar). REST do back-end (`/chat/auto-replies`, `/chat/quick-replies`).
 *
 * Âmbitos: as GERAIS (`userAppId: null`) valem para toda a agência e só quem
 * tem `canAssign` as altera — sem essa capacidade ficam em leitura; as
 * PRÓPRIAS (`userAppId` do utilizador) são sempre editáveis e ganham às
 * gerais. Respostas de outros consultores que o servidor devolva ao diretor
 * aparecem só em leitura na lista de rápidas.
 *
 * Erros: o servidor responde com chaves `t-chat-*`, que o ApiErrorService
 * traduz (ou devolve tal como vêm quando já são texto).
 */
@Component({
  selector: 'app-chat-settings',
  templateUrl: './chat-settings.component.html',
  styleUrls: ['./chat-settings.component.scss']
})
export class ChatSettingsComponent implements OnInit, OnDestroy {
  strings: ChatStrings = CHAT_STRINGS_PT;
  capabilities: ChatCapabilities = CHAT_NO_CAPABILITIES;
  triggers = CHAT_AUTO_REPLY_TRIGGERS;
  generalRows: ChatAutoReplyRow[] = [];
  mineRows: ChatAutoReplyRow[] = [];
  loadingAuto = false;
  savingAuto = false;
  quickReplies: ChatQuickReply[] = [];
  loadingQuick = false;
  savingQuick = false;
  quickForm: QuickReplyForm = this.emptyQuickForm();
  readonly chatLink = ['/chat'];
  private subscriptions = new Subscription();
  private modalRef: NgbModalRef | null = null;

  constructor(
    private chatService: ChatService,
    private modalService: NgbModal,
    private apiError: ApiErrorService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.chatService.capabilities$.subscribe(caps => {
      const before = this.meId;
      this.capabilities = caps || CHAT_NO_CAPABILITIES;
      // O "eu" chegou (ou mudou): as linhas "as minhas" são recalculadas.
      if (this.meId !== before && this.generalRows.length > 0) {
        this.loadAutoReplies();
      }
    }));
    this.loadAutoReplies();
    this.loadQuickReplies();
  }

  /**
   * A identidade de chat deste socket (`chat_capabilities.userAppId`), nunca o
   * `userApp` do login: um diretor cuja conta de chat nasceu depois não o tem,
   * e sem "eu" as respostas dos outros pareciam editáveis.
   */
  get meId(): string | undefined {
    return this.capabilities.userAppId ? String(this.capabilities.userAppId) : undefined;
  }

  /** As automáticas só existem para quem as pode usar; enquanto a identidade não chega mostra-se. */
  get showAutoReplies(): boolean {
    return !this.capabilities.chatIdentity || !!this.capabilities.canUseAutoReplies;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.modalRef) {
      this.modalRef.dismiss();
      this.modalRef = null;
    }
  }

  /* ---------------------------------------------------------------------
   * respostas automáticas
   * ------------------------------------------------------------------ */

  /** As gerais só se alteram com canAssign; as próprias sempre. */
  get canEditGeneral(): boolean {
    return !!this.capabilities.canAssign;
  }

  get hasGeneral(): boolean {
    return this.generalRows.some(r => !!r.existing);
  }

  private loadAutoReplies(): void {
    this.loadingAuto = true;
    this.subscriptions.add(this.chatService.getAutoReplies().subscribe({
      next: list => {
        const rows = list || [];
        this.generalRows = this.triggers.map(t => this.buildRow(t, 'general', rows.find(r => r.general && r.trigger === t) || null));
        const me = this.meId;
        this.mineRows = this.triggers.map(t => this.buildRow(t, 'mine',
          rows.find(r => !r.general && r.trigger === t && !!me && String(r.userAppId) === me) || null));
        this.loadingAuto = false;
      },
      error: err => {
        this.loadingAuto = false;
        this.generalRows = this.triggers.map(t => this.buildRow(t, 'general', null));
        this.mineRows = this.triggers.map(t => this.buildRow(t, 'mine', null));
        this.showError(err, this.strings.errorTitle);
      }
    }));
  }

  private buildRow(trigger: ChatAutoReplyTrigger, scope: 'general' | 'mine', existing: ChatAutoReply | null): ChatAutoReplyRow {
    return {
      trigger,
      scope,
      existing,
      message: existing ? existing.message || '' : '',
      status: existing ? existing.status !== false : true,
      dirty: false,
      saving: false,
      saved: false
    };
  }

  isRowEditable(row: ChatAutoReplyRow): boolean {
    return row.scope === 'mine' || this.canEditGeneral;
  }

  markDirty(row: ChatAutoReplyRow): void {
    row.dirty = true;
    row.saved = false;
  }

  hasDirty(rows: ChatAutoReplyRow[]): boolean {
    return rows.some(r => r.dirty);
  }

  /**
   * Guarda as linhas alteradas de uma secção: PUT quando já existe, POST
   * quando é nova (`userAppId: null` para uma geral), DELETE quando o texto
   * ficou vazio. Uma linha nova sem texto não faz nada.
   */
  saveSection(rows: ChatAutoReplyRow[]): void {
    const dirty = rows.filter(r => r.dirty && this.isRowEditable(r));
    if (dirty.length === 0 || this.savingAuto) {
      return;
    }
    this.savingAuto = true;
    dirty.forEach(r => r.saving = true);
    const requests = dirty.map(row => this.saveRow(row));
    this.subscriptions.add(forkJoin(requests).subscribe({
      next: () => {
        this.savingAuto = false;
        // Toast do projeto (ngx-toastr): só quando nenhuma linha ficou por guardar.
        if (dirty.every(r => !r.dirty)) {
          this.toast(this.strings.savedToast);
        }
      },
      error: () => this.savingAuto = false
    }));
  }

  /** Feedback curto de "guardado", com o toastr do CRM (o forRoot é "fixo": aqui dá-se um tempo). */
  private toast(message: string): void {
    this.toastr.success(message, undefined, { timeOut: 2500, extendedTimeOut: 1000, disableTimeOut: false, closeButton: true, progressBar: true });
  }

  /** Nunca falha para o forkJoin: cada linha trata o seu erro e devolve `of(null)`. */
  private saveRow(row: ChatAutoReplyRow): Observable<any> {
    const message = (row.message || '').trim();
    const done = (reply: ChatAutoReply | null) => {
      row.existing = reply;
      row.message = reply ? reply.message || '' : '';
      row.status = reply ? reply.status !== false : true;
      row.dirty = false;
      row.saving = false;
      row.saved = true;
    };
    const fail = (err: any) => {
      row.saving = false;
      this.showError(err, this.strings.saveFailed);
      return of(null);
    };
    return new Observable<any>(observer => {
      let request: Observable<any>;
      if (!message) {
        if (!row.existing) {
          done(null);
          observer.next(null);
          observer.complete();
          return;
        }
        request = this.chatService.deleteAutoReply(row.existing.id);
        request.subscribe({
          next: () => { done(null); observer.next(null); observer.complete(); },
          error: err => { fail(err); observer.next(null); observer.complete(); }
        });
        return;
      }
      request = row.existing
        ? this.chatService.updateAutoReply(row.existing.id, { message, status: row.status })
        : this.chatService.createAutoReply({
          trigger: row.trigger,
          message,
          status: row.status,
          ...(row.scope === 'general' ? { userAppId: null } : {})
        });
      request.subscribe({
        next: (reply: ChatAutoReply) => { done(reply); observer.next(reply); observer.complete(); },
        error: err => { fail(err); observer.next(null); observer.complete(); }
      });
    });
  }

  /* ---------------------------------------------------------------------
   * respostas rápidas
   * ------------------------------------------------------------------ */

  private loadQuickReplies(): void {
    this.loadingQuick = true;
    this.subscriptions.add(this.chatService.getQuickReplies(true).subscribe({
      next: list => {
        this.quickReplies = (list || []).slice().sort((a, b) => String(a.shortcut).localeCompare(String(b.shortcut)));
        this.loadingQuick = false;
      },
      error: err => {
        this.loadingQuick = false;
        this.showError(err, this.strings.errorTitle);
      }
    }));
  }

  /** Própria ou, com canAssign, geral. As de outros consultores ficam em leitura. */
  canEditQuick(r: ChatQuickReply): boolean {
    if (r.general) {
      return this.canEditGeneral;
    }
    return !!this.meId && String(r.userAppId) === this.meId;
  }

  trackByQuick = (_i: number, r: ChatQuickReply) => r.id;

  openQuickModal(tpl: TemplateRef<any>, reply?: ChatQuickReply): void {
    this.quickForm = reply
      ? { id: reply.id, shortcut: reply.shortcut || '', message: reply.message || '', status: reply.status !== false, general: !!reply.general }
      : this.emptyQuickForm();
    if (this.modalRef) {
      this.modalRef.dismiss();
    }
    this.modalRef = this.modalService.open(tpl, { centered: true, size: 'md' });
    this.modalRef.result.then(() => this.modalRef = null, () => this.modalRef = null);
  }

  get quickFormValid(): boolean {
    const shortcut = (this.quickForm.shortcut || '').trim();
    return /^\/[a-z0-9_-]{1,39}$/.test(shortcut) && !!(this.quickForm.message || '').trim();
  }

  saveQuick(): void {
    if (!this.quickFormValid || this.savingQuick) {
      return;
    }
    this.savingQuick = true;
    const form = this.quickForm;
    const body = { shortcut: form.shortcut.trim(), message: form.message.trim(), status: form.status };
    const request = form.id
      ? this.chatService.updateQuickReply(form.id, body)
      : this.chatService.createQuickReply({ ...body, ...(form.general ? { userAppId: null } : {}) });
    this.subscriptions.add(request.subscribe({
      next: () => {
        this.savingQuick = false;
        if (this.modalRef) {
          this.modalRef.close();
          this.modalRef = null;
        }
        this.toast(this.strings.savedToast);
        this.loadQuickReplies();
      },
      error: err => {
        this.savingQuick = false;
        this.showError(err, this.strings.saveFailed);
      }
    }));
  }

  deleteQuick(r: ChatQuickReply): void {
    if (!this.canEditQuick(r)) {
      return;
    }
    Swal.fire({
      title: this.strings.confirmTitle,
      text: r.shortcut,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.strings.confirmDelete,
      cancelButtonText: this.strings.cancel,
      confirmButtonColor: '#f06548'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      this.subscriptions.add(this.chatService.deleteQuickReply(r.id).subscribe({
        next: () => {
          this.quickReplies = this.quickReplies.filter(x => x.id !== r.id);
          this.toast(this.strings.deletedToast);
        },
        error: err => this.showError(err, this.strings.deleteFailed)
      }));
    });
  }

  private emptyQuickForm(): QuickReplyForm {
    return { id: null, shortcut: '/', message: '', status: true, general: false };
  }

  /* ---------------------------------------------------------------------
   * diálogos
   * ------------------------------------------------------------------ */

  private showError(err: any, fallback: string): void {
    const resolved = this.apiError.resolve(err, fallback);
    Swal.fire({
      title: this.strings.errorTitle,
      text: resolved.message,
      icon: 'warning',
      confirmButtonText: this.strings.close,
      confirmButtonColor: '#405189'
    });
  }
}
